import React, { useState } from 'react';
import { getInspectionSignatureSources, normalizeMediaDataUrl } from '@/utils/inspectionDisplay';

/**
 * Renders responsible-party signature with normalized data URLs and readable contrast.
 * Some mobile builds export white ink on a black canvas; invert restores visibility on white UI.
 */
export default function InspectionSignatureSection({ inspection, title, alt }) {
  const { imageUrl, signerName, signerId, signedAt, ipAddress, proofLocation } =
    getInspectionSignatureSources(inspection);
  const [invert, setInvert] = useState(false);

  if (!imageUrl && !signerName && !signerId) return null;

  const handleLoad = (e) => {
    try {
      const img = e.target;
      const w = Math.min(img.naturalWidth || 0, 64);
      const h = Math.min(img.naturalHeight || 0, 64);
      if (w < 4 || h < 4) return;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let dark = 0;
      let light = 0;
      for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (lum < 80) dark += 1;
        if (lum > 200) light += 1;
      }
      const pixels = data.length / 4;
      if (dark > pixels * 0.55 && light > pixels * 0.02) {
        setInvert(true);
      }
    } catch (_) {
      /* cross-origin or tainted canvas — keep default */
    }
  };

  const formatSignedAt = (raw) => {
    if (!raw) return '';
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw).slice(0, 19);
      return d.toLocaleString();
    } catch {
      return String(raw).slice(0, 19);
    }
  };

  return (
    <div>
      <h4 className="font-semibold mb-3">{title}</h4>
      <div className="flex flex-wrap items-start gap-4">
        {imageUrl && (
          <img
            src={normalizeMediaDataUrl(imageUrl)}
            alt={alt}
            onLoad={handleLoad}
            className="h-20 max-w-[280px] object-contain bg-white border rounded p-1"
            style={invert ? { filter: 'invert(1)' } : undefined }
          />
        )}
        <div className="space-y-1 text-sm text-gray-700">
          {signerName ? (
            <div>
              <span className="text-gray-500">Nombre: </span>
              <span className="font-medium text-gray-800">{signerName}</span>
            </div>
          ) : null}
          {signerId ? (
            <div>
              <span className="text-gray-500">DNI / NIE: </span>
              <span className="font-medium text-gray-800 tracking-wide">{signerId}</span>
            </div>
          ) : (
            <div className="text-amber-700 text-xs">
              DNI no registrado en esta peritación (se captura en recogidas/entregas nuevas).
            </div>
          )}
          {signedAt ? (
            <div className="text-xs text-gray-500">Fecha/hora: {formatSignedAt(signedAt)}</div>
          ) : null}
          {proofLocation ? (
            <div className="text-xs text-gray-500">Ubicación: {proofLocation}</div>
          ) : null}
          {ipAddress ? (
            <div className="text-xs text-gray-500">IP: {ipAddress}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
